<template>
    <div class="c-admissions-calendar">
        <Loader v-if="isLoading"/>

        <p class='c-col-text'>Please select a day that you would like to visit the Museum</p>

        <FullCalendar
            id="fullCalendar"
            ref='fullCalendar'
            :options="calendarOptions"
        />

        <div id="admissionInstances" ref="admissionInstances" :aria-hidden="!showAdmissionInstances" tabindex="0">
            <AdmissionInstances
                v-if="showAdmissionInstances"
                :admission="currentAdmission"
                :openingTimes="openingTimes"
            />
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import OpeningTimes from '../modules/OpeningTimes'
import AdmissionInstances from './AdmissionInstances'
import Loader from './Loader'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import momentPlugin from '@fullcalendar/moment'
import moment from 'moment'

export default {
    name: 'Admissions',
    props: {
        firstAdmission: String,
        lastAdmission: String,
    },
    components: {
        AdmissionInstances,
        FullCalendar,
        Loader
    },
    data() {
        return {
            calendarOptions: {
                firstDay: 0,
                headerToolbar: {
                    start: 'prev',
                    center: 'title',
                    end: 'next'
                },
                buttonIcons: false,
                buttonText: {
                    prev: '',
                    next: '',
                },
                dayMaxEventRows: true,
                views: {
                    dayGridMonth: {
                        dayMaxEventRows: 6,
                        dayHeaderFormat: {
                            weekday: 'narrow'
                        }
                    }
                },
                footerToolbar: false,
                height: 'auto',
                plugins: [dayGridPlugin, interactionPlugin, momentPlugin],
                initialView: 'dayGridMonth',
                events: [],
                displayEventTime: false,
                validRange: this.validRange,
                fixedWeekCount: false,
                timeZone: 'UTC',
                dayCellClassNames: ['fc-day-no-admissions'], // This class will denote days with not event
                eventClick: (info) => {
                    this.$emitter.emit('event-selected', info)
                },
                eventDidMount: (info) => {
                    // When event has been added the no admission class will be remvoed
                    info.el.closest('.fc-day').classList.remove('fc-day-no-admissions')
                }
            },
            isLoading: true,
            calendarApi: null,
            openingTimes: null,
            currentAdmission: null,
            filterDates: window.admissions.filterDates
        }
    },
    mounted() {
        this.calendarApi = this.$refs.fullCalendar.getApi()
        this.resizeOnModalOpen()
        this.configNextAndPrevBtns()
        this.addEventClickHandler()
        this.fetchOpeningTimes()
        this.fetchAdmissions()
    },
    methods: {
        addEventClickHandler() {
            // Handler for showing instance card for the selected date/event
            this.$emitter.on('event-selected', (info) => {
                info.jsEvent.preventDefault()

                document.querySelector('.fc-daygrid-event.selected')
                    ?.classList.remove('selected')
                    
                this.currentAdmission = {
                    datetime: moment.utc(info.event.start).format('YYYY-MM-DD'),
                    instances: info.event.extendedProps.instances,
                }

                this.scrollToAdmissionInstances()
            })
        },
        configNextAndPrevBtns() {
            const previousButton = document.querySelector('#admission-dialog .fc-prev-button')
            previousButton.removeAttribute('title')
            previousButton.innerHTML = '<span class="u-hidden-visually">Go to previous month</span><i class="fa-light fa-chevron-left" aria-hidden="true"></i>'
            previousButton.addEventListener('click', () => {
                this.fetchAdmissions()
            })

            const nextButton = document.querySelector('.fc-next-button')
            nextButton.removeAttribute('title')
            nextButton.innerHTML = '<span class="u-hidden-visually">Go to next month</span><i class="fa-light fa-chevron-right" aria-hidden="true"></i>'
            nextButton.addEventListener('click', () => {
                this.fetchAdmissions()
            })
        },
        fetchAdmissions() {
            this.isLoading = true
            this.calendarOptions.events = []
            this.currentAdmission = null

            const endpoint = wpApiSettings.root + 'v1/attendable/calendar'
            axios.get(endpoint, {
                params: this.queryParams(),
                headers: {
                    'X-WP-Nonce': wpApiSettings.nonce,
                }
            }).then((response) => {
                let events = response.data

                // Filtering to match the instance dates of a Ticket Exhibition event
                if (this.filterDates.length) {
                    events = this.filterEvents(events)
                }

                this.calendarOptions.events = events
                
                this.isLoading = false
            }).catch((error) => {
                this.isLoading = false
                console.warn(error)
            })
        },
        fetchOpeningTimes() {
            // Opening retrieve when Admission component is mounted
            // This will be used in the AdmissionInstances component
            const endpoint = wpApiSettings.root + 'v1/opening-times/'
            axios.get(endpoint)
                .then((response) => {
                    const data = response.data
                    this.openingTimes = new OpeningTimes(data.days, data.overrides)
                }).catch((error) => {
                    console.log(error)
                })
        },
        filterEvents(events) {
            return events.filter((event) => this.filterDates.includes(event.start))
        },
        resizeOnModalOpen() {
            // This event correct an issue with the calendar not rendering the correct size
            // This is due to the calendar being in modal that is hidden when calendar is rendered
            this.$emitter.on('state--modal-open', (e) => {
                this.calendarApi.updateSize()
            })
        },
        queryParams() {
            const today = moment();
            let start = moment.utc(this.calendarApi.currentData.currentDate)

            // Will prevent past instances from being retrieved
            start = start.isBefore(today) ? today : start

            return {
                start: start.format('YYYY-MM-DD 00:00:00'),
                end: moment.utc(this.calendarApi.currentData.currentDate).endOf("month").format('YYYY-MM-DD 23:59:59'),
                event: window.admissions.event
            }
        },
        scrollToAdmissionInstances() {
            const admissionInstances = this.$refs.admissionInstances;
            admissionInstances.scrollIntoView({
                behavior: "smooth",
                inline: "end"
            });

            admissionInstances.focus();

            // ickle timeout so focus happens before blurring
            setTimeout(() => {
                admissionInstances.blur(); 
            }, 100); 
        },
        validRange() {
            return {
                start: moment.utc(this.firstAdmission).startOf("month").format('YYYY-MM-DD 00:00:00'),
                end: moment.utc(this.lastAdmission).endOf("month").format('YYYY-MM-DD 23:59:59'),
            }
        }
    },
    computed: {
        showAdmissionInstances() {
            return this.currentAdmission !== null
        }
    }
}
</script>
